import { type ReactNode, useState } from 'react';
import { isBoolean, isNotNullish } from '@chainflip/utils/guard';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import classNames from 'classnames';
import { MacScrollbar } from 'mac-scrollbar';
import { Divider } from '@/shared/components/Divider';
import Checkbox from '@/shared/components/flip-ui-kit/Checkbox';
import ChevronIcon from '@/shared/icons/flip-ui-kit/large/Chevron';

export type DropdownOption = {
  icon?: ReactNode;
  label: ReactNode;
  description?: ReactNode;
  disabled?: boolean;
  onClick: () => void;
  checked?: boolean;
};

export const Dropdown = ({
  options,
  buttonContent,
  buttonContentClassname,
  contentClassname,
  scrollClassname,
  showChevronIcon = false,
  chevronIconClassname,
  disabled = false,
  align = 'end',
  sideOffset = 6,
  onReset,
}: {
  options: readonly DropdownOption[];
  buttonContent: ReactNode;
  buttonContentClassname?: string;
  contentClassname?: string;
  scrollClassname?: string;
  showChevronIcon?: boolean;
  chevronIconClassname?: string;
  disabled?: boolean;
  align?: 'center' | 'end' | 'start';
  sideOffset?: number;
  onReset?: () => void;
}) => {
  const [open, setOpen] = useState(false);
  const numberOfSelectedOptions = options.filter((item) => item.checked).length;

  return (
    <DropdownMenu.Root
      open={open}
      onOpenChange={(value) => {
        if (!disabled) setOpen(value);
      }}
      modal={false}
    >
      <DropdownMenu.Trigger asChild>
        <button
          type="button"
          disabled={disabled}
          className={classNames(
            'flex items-center justify-center rounded-[4px] border border-cf-gray-4 bg-cf-gray-3 text-cf-light-3 shadow-grayPop3 outline-none transition hover:bg-cf-gray-4 hover:text-cf-white data-[state=open]:bg-cf-gray-4 data-[state=open]:text-cf-white',
            buttonContentClassname,
          )}
          onClick={(e) => {
            e.stopPropagation(); // prevent triggering row click handlers when used in tables
          }}
        >
          {buttonContent}
          {showChevronIcon && (
            <ChevronIcon
              flip={!open}
              className={classNames(
                chevronIconClassname ?? 'text-cf-white group-hover:text-cf-white',
                disabled && 'text-cf-light-1',
                'transition ',
              )}
            />
          )}
        </button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          className={classNames(
            'z-20 flex flex-col rounded-lg border border-cf-gray-4 bg-cf-gray-3-5 text-13 shadow-grayPop2',
            contentClassname,
          )}
          sideOffset={sideOffset}
          align={align}
        >
          <MacScrollbar
            skin="dark"
            className={classNames('max-h-[200px] overflow-y-scroll', scrollClassname)}
          >
            {numberOfSelectedOptions > 0 && onReset && (
              <>
                <div className="flex items-center justify-between p-3 font-aeonikMedium">
                  <span className="text-cf-light-4">{numberOfSelectedOptions} selected</span>
                  <button type="button" className="text-cf-green-3" onClick={onReset}>
                    Unselect all
                  </button>
                </div>
                <Divider className="h-px" />
              </>
            )}
            {options.map((option, index) => (
              <DropdownMenu.Item
                disabled={option.disabled}
                // eslint-disable-next-line react/no-array-index-key
                key={index}
                asChild={!isBoolean(option.checked)}
                className="outline-none"
              >
                <button
                  type="button"
                  disabled={option.disabled}
                  className={classNames(
                    !option.disabled && 'hover:bg-cf-gray-4',
                    index === 0 && !numberOfSelectedOptions && 'hover:rounded-t-md',
                    index === options.length - 1 && 'hover:rounded-b-md',
                    'group flex w-full items-center gap-2 whitespace-nowrap p-3 outline-none',
                  )}
                  onClick={(e) => {
                    e.stopPropagation();
                    option.onClick();
                  }}
                >
                  {isNotNullish(option.checked) && (
                    <Checkbox
                      id={index.toString()}
                      setChecked={option.onClick}
                      checked={option.checked}
                    />
                  )}
                  {option.icon && <div className="text-cf-light-2">{option.icon}</div>}
                  <div
                    className={classNames(
                      option.disabled ? 'text-cf-gray-5' : 'text-cf-light-4',
                      'font-aeonikMedium',
                    )}
                  >
                    {option.label}
                  </div>
                  {option.description && (
                    <div className="text-cf-light-2">{option.description}</div>
                  )}
                </button>
              </DropdownMenu.Item>
            ))}
          </MacScrollbar>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
};
